import { ContentPanel } from '@/components/content-panel';
import { CustomContentSection } from '@/components/custom-content-section';
import { useLayoutContext } from '@/contexts';
import { Flex, Text } from '@mantine/core';
import { useNavigate, useSearch } from '@tanstack/react-router';
import { FormikProps } from 'formik';
import { useCallback, useMemo, useRef } from 'react';
import {
	ContentSectionLocation,
	ContentSectionPlacement,
} from '../../../../backend/src/types.generated';
import { StandardButton } from '../../components/buttons/standard';
import { PrimaryLayout } from '../../layouts/primary';
import { ResetPasswordForm, ResetPasswordValues } from './form';
import { useSendResetPasswordLinkMutation } from './graphql.generated';

export function RequestPasswordResetPage() {
	const navigate = useNavigate();
	const { email } = useSearch({ from: '/request-password-reset' });
	const [sendResetPasswordLink, { data, loading, error }] = useSendResetPasswordLinkMutation();
	const { isDesktop } = useLayoutContext();

	const getMessage = () => {
		if (loading) {
			return 'Sending email...';
		}

		if (error) {
			return 'An error occurred, please try again later';
		}

		if (data?.sendResetPasswordLink === true) {
			return 'If your email was associated with a registered account then a password reset link has been sent';
		}

		if (data?.sendResetPasswordLink === false) {
			throw new Error('This should never happen.');
		}

		return "Enter your account email address and you'll receive a link to reset your password";
	};

	const onSubmit = useCallback(
		async (values: ResetPasswordValues) => {
			const { data } = await sendResetPasswordLink({
				variables: { username: values.email.toLowerCase() },
			});

			if (data?.sendResetPasswordLink) {
				navigate({ to: '/password-link-sent', params: { email: values.email.toLowerCase() } });
			}
		},
		[navigate, sendResetPasswordLink]
	);

	const formRef = useRef<FormikProps<ResetPasswordValues>>(null);
	const triggerSubmit = useCallback(() => {
		formRef.current?.submitForm();
	}, [formRef]);

	const resetPasswordButton = useMemo(
		() => (
			<StandardButton variant="filled" onClick={triggerSubmit}>
				Reset Password
			</StandardButton>
		),
		[triggerSubmit]
	);

	return (
		<PrimaryLayout
			header={{ title: 'Reset Password', backTo: '/auth/login' }}
			footer={{ children: resetPasswordButton }}
		>
			<CustomContentSection
				location={ContentSectionLocation.RequestPasswordReset}
				placement={ContentSectionPlacement.Top}
			/>
			<ContentPanel gap="md" justify="center" align="center" flex={1}>
				<Text size="sm" fw={400}>
					{getMessage()}
				</Text>
				<ResetPasswordForm onSubmit={onSubmit} formRef={formRef} email={email?.toLowerCase()} />
			</ContentPanel>
			<CustomContentSection
				location={ContentSectionLocation.RequestPasswordReset}
				placement={ContentSectionPlacement.Middle}
			/>

			{/* The Reset password button in the footer is only visible on mobile, so we need it here on desktop. */}
			{isDesktop && (
				<Flex w="100%" justify="center" align="center">
					<Flex w={220}>{resetPasswordButton}</Flex>
				</Flex>
			)}

			<CustomContentSection
				location={ContentSectionLocation.RequestPasswordReset}
				placement={ContentSectionPlacement.Bottom}
			/>
		</PrimaryLayout>
	);
}
